import { useQuery } from '@apollo/client';
import { useRef, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { apiContract } from '@eluve/api-contract';
import { HStack, Input, NewButton, useToast } from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useAnalytics } from '@eluve/posthog-react';
import {
  useAssignedTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import { userSignatureFileFragment } from '@eluve/shared-gql-operations';
import { useUploadUserFile } from '@eluve/smart-blocks';

const getUserSignatureQuery = graphql(
  `
    query getUserSignature($userId: uuid!) {
      usersByPk(id: $userId) {
        ...UserSignatureFile
      }
    }
  `,
  [userSignatureFileFragment],
);

export const SignatureSettings = () => {
  const toast = useToast();
  const [isUpdating, setIsUpdating] = useState(false);
  const signatureUrl = useRef<string | undefined>(undefined);
  const userId = useUserIdFromSession();
  const tenantId = useAssignedTenantIdFromParams();
  const apiClient = useApiClient();
  const { uploadUserFileToGCS } = useUploadUserFile(tenantId);

  const analytics = useAnalytics('user_settings');

  const { data, loading, refetch } = useQuery(getUserSignatureQuery, {
    variables: { userId },
    notifyOnNetworkStatusChange: true,
  });

  const signatureFile = data?.usersByPk?.userFiles?.[0];

  if (signatureFile && !loading) {
    signatureUrl.current = apiClient.getEndpointUrl(
      apiContract.users.downloadUserFile,
      {
        tenantId,
        userId,
        fileId: signatureFile.fileId,
      },
    );
  }

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    try {
      setIsUpdating(true);

      const file = event.target.files?.[0];
      const extension = file?.name.split('.').pop();
      if (!file || !extension) {
        toast.error('Invalid file extension.');
        return;
      }

      const userUploadId = await uploadUserFileToGCS({
        file,
        mimeType: 'image/*',
        extension,
        fileType: 'SIGNATURE',
      });

      if (!userUploadId) {
        toast.error('We could not upload the file.');
        return;
      }

      analytics.capture('signature_update', {
        tenantId,
        userId,
      });

      refetch();
    } catch {
      toast.error('We could not update your signature.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleFileDelete = async () => {
    if (!signatureFile?.fileId) {
      return;
    }

    setIsUpdating(true);

    await apiClient.users.markUserFileUploadDeleted({
      body: { userUploadId: signatureFile.fileId },
      params: { tenantId },
    });

    analytics.capture('signature_delete', {
      tenantId,
      userId,
    });

    refetch();

    signatureUrl.current = undefined;

    setIsUpdating(false);
  };

  return (
    <div className="sm:w-1/2 sm:pr-1">
      {signatureUrl.current ? (
        <HStack
          className="rounded-lg border border-border-primary bg-bg-primary p-4"
          gap={4}
          justify="between"
        >
          <img
            alt="Signature"
            className="max-h-20 min-w-0"
            src={signatureUrl.current}
          />
          <NewButton
            disabled={loading || isUpdating}
            icon={{ name: 'Trash' }}
            onClick={handleFileDelete}
            type="outline"
          />
        </HStack>
      ) : (
        <Input
          accept="image/*"
          className="rounded-lg border border-border-primary bg-bg-primary"
          disabled={loading || isUpdating}
          onChange={handleFileChange}
          type="file"
        />
      )}
    </div>
  );
};
